/**
 * Whether this advisor has agreed to what Waterlily does with their CRM, and how they agree.
 *
 * Two consents, because they authorise different things: connecting the CRM at all (which includes
 * the note Waterlily writes back into it), and reading their entire contact list for prospecting.
 * An advisor may reasonably want the first without the second.
 *
 * The wording comes from the server's consent catalogue plus a per-provider sentence, because note
 * visibility genuinely differs — Salesforce notes are visible to the whole organisation, Wealthbox
 * notes are private. Whatever is shown is sent back and stored verbatim, so the record says what the
 * advisor actually agreed to rather than only which consent id it was.
 *
 * The record is saved by the main API, but the integrations service is what acts on it, and it
 * remembers the last answer for a minute. Every save therefore tells it the answer changed, so a
 * withdrawal stops reads straight away and a new consent can be used straight away.
 *
 * A lookup that failed is reported as a failure. The two consent flags read "no" in that case, and a
 * caller that locks something on a "no" needs to tell it from an answer it could not get.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { crmConsentsApi, crmConsentsKeys } from '@client/api/waterlily';
import { prospectingApi } from '@client/api/integrations';
import { CRM_NOTE_VISIBILITY_DISCLOSURE } from '@shared';
import { getUserIpAddress } from '@/util/getUserIpAddress';

export function crmNoteDisclosure(provider: string): string {
  return CRM_NOTE_VISIBILITY_DISCLOSURE[provider] ?? '';
}

export function useCrmConsent({ advisorId, provider }: { advisorId: string; provider: string }) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: crmConsentsKeys.byAdvisor(advisorId),
    queryFn: () => crmConsentsApi.getByAdvisorId(advisorId),
    enabled: !!advisorId,
  });

  const forProvider = query.data?.consents.find(consent => consent.provider === provider);

  const mutation = useMutation({
    mutationFn: async ({
      kind,
      shownText,
      hasConsented = true,
    }: {
      kind: 'connection' | 'bookRead';
      shownText: string;
      hasConsented?: boolean;
    }) => {
      // Best effort: a missing IP is worth less than a missing consent record.
      let ipAddress: string | null = null;
      try {
        ipAddress = await getUserIpAddress();
      } catch {
        ipAddress = null;
      }

      const saved = await crmConsentsApi.save({
        advisorId,
        provider,
        kind,
        hasConsented,
        shownText,
        ipAddress,
        userAgent: navigator.userAgent,
      });

      // Best effort: the consent is already recorded, and the remembered answer expires by itself.
      await prospectingApi.consent.changed().catch(() => undefined);

      return saved;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: crmConsentsKeys.byAdvisor(advisorId) }),
  });

  return {
    isLoading: query.isLoading,
    isError: query.isError,
    hasConnectionConsent: forProvider?.hasConnectionConsent ?? false,
    hasBookReadConsent: forProvider?.hasBookReadConsent ?? false,
    bookReadConsentedAt: forProvider?.bookReadConsentedAt ?? null,
    noteDisclosure: crmNoteDisclosure(provider),
    give: (kind: 'connection' | 'bookRead', shownText: string) =>
      mutation.mutateAsync({ kind, shownText }),
    withdraw: (kind: 'connection' | 'bookRead', shownText: string) =>
      mutation.mutateAsync({ kind, shownText, hasConsented: false }),
    isSaving: mutation.isPending,
    error: mutation.error,
    clearError: () => mutation.reset(),
  };
}
